import {
  GoogleMap,
  LoadScriptNext,
  Polygon,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import React, { useEffect, useState } from "react";
import { MapKey } from "../../../Data/Data";
import { makeRequest } from "../../../api";
import CircularProgressLoader from "../../CircularProgressLoader";

const HerbivoreMap = ({
  data,
  setData,
  loading,
  setLoading,
  api,
  selectedYear,
}) => {
  const [position, setPosition] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [adjustCoordinatesForOverlap, setAdjustCoordinatesForOverlap] =
    useState([]);
  const handleMapLoad = (map) => {
    setPosition({
      lat: map.latLng.lat(),
      lng: map.latLng.lng(),
    });
  };

  const onLoad = (map) => {
    setMapRef(map);
  };

  const handleDragEnd = () => {
    if (mapRef) {
      const newCenter = mapRef.getCenter();
      setPosition({
        lat: newCenter.lat(),
        lng: newCenter.lng(),
      });
    }
  };

  const getHeatMapColor = (herbCount, maxHerbCount) => {
    const normalized = herbCount / maxHerbCount;
    const r = 255;
    const g = Math.round(255 * (1 - normalized));
    const b = 0;
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Function to create custom marker with text
  const createMarkerIcon = (herbCount, maxHerbCount) => {
    const color = getHeatMapColor(herbCount, maxHerbCount);

    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 10, // Fixed size for all markers
      fillColor: color,
      fillOpacity: 0.8,
      strokeWeight: 0,
      strokeColor: "#FFFFFF",
    };
  };

  // Get the maximum herb count for heat map scaling
  const maxHerbCount = Math.max(...data.map((item) => item.herb_count), 0);

  // Function to adjust coordinates to prevent marker overlap
  const adjustCoordinatesForOverlapSetter = (data) => {
    const adjustedData = [...data];
    const minDistance = 0.001; // Minimum distance between markers (approx 100m)

    for (let i = 0; i < adjustedData.length; i++) {
      for (let j = i + 1; j < adjustedData.length; j++) {
        const item1 = adjustedData[i];
        const item2 = adjustedData[j];

        // Calculate distance between two points
        const latDiff = Math.abs(item1.latitude - item2.latitude);
        const lngDiff = Math.abs(item1.longitude - item2.longitude);
        const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);

        // If markers are too close, adjust the second marker's position
        if (distance < minDistance) {
          const angle = Math.random() * 2 * Math.PI; // Random angle
          const offsetDistance = minDistance * 10; // Slightly more than minimum

          adjustedData[j] = {
            ...item2,
            latitude: item2.latitude + Math.cos(angle) * offsetDistance,
            longitude: item2.longitude + Math.sin(angle) * offsetDistance,
          };
        }
      }
    }

    return adjustedData;
  };

  useEffect(() => {
    setAdjustCoordinatesForOverlap(
      adjustCoordinatesForOverlapSetter(
        data.filter((item) => item.herb_count > 0)
      )
    );
  }, [selectedYear]);

  return (
    <>
      {loading ? (
        <div
          style={{ width: "50vw", height: "60vh" }}
          className="loading-container"
        >
          <div className="loading-content">
            <CircularProgressLoader className="loading-spinner" size={48} />
            <p>Loading Map...</p>
          </div>
        </div>
      ) : (
        <div style={{ height: "60vh" }} className="Map-View">
          <LoadScriptNext googleMapsApiKey={MapKey}>
            <GoogleMap
              mapContainerStyle={{ width: "95%", height: "100%" }}
              center={
                position === null ? { lat: 21.572004, lng: 79.19164 } : position
              }
              zoom={11}
              onClick={handleMapLoad}
              onLoad={onLoad}
              onDragEnd={handleDragEnd}
            >
              <Polygon
                paths={api.polygonCoordinates}
                options={{
                  fillColor: "skyblue",
                  fillOpacity: 0.4,
                  strokeColor: "blue",
                  strokeOpacity: 0.6,
                  strokeWeight: 2,
                }}
                onClick={handleMapLoad}
              />

              {/* Render all markers with heat map colors and fixed size */}
              {adjustCoordinatesForOverlap.map((item) => (
                <React.Fragment key={item.camera_id}>
                  <Marker
                    position={{
                      lat: item.latitude,
                      lng: item.longitude,
                    }}
                    icon={createMarkerIcon(item.herb_count, maxHerbCount)}
                    onClick={() => setSelectedMarker(item)}
                    label={{
                      text: item.herb_count.toString(),
                      color: "#000000",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  />
                </React.Fragment>
              ))}

              {/* InfoWindow on marker click */}
              {selectedMarker && (
                <InfoWindow
                  position={{
                    lat: selectedMarker.latitude,
                    lng: selectedMarker.longitude,
                  }}
                  onCloseClick={() => setSelectedMarker(null)}
                >
                  <div>
                    <strong>Camera: {selectedMarker.camera_name}</strong>
                    <br />
                    Herb Count: {selectedMarker.herb_count}
                    <br />
                    <small>Max Count: {maxHerbCount}</small>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </LoadScriptNext>
        </div>
      )}
    </>
  );
};

export default HerbivoreMap;
